import { Injectable, Injector, OnDestroy, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  NavigationEnd,
  RouteReuseStrategy,
  Router,
} from '@angular/router';
import { isReusableRoute } from '@navstation-portal/shared/util/navigation';
import { Subscription, filter } from 'rxjs';

/**
 * Caches and reattaches the vessel-report tab routes so switching between
 * already-visited tabs is instant (the live DOM subtree is reattached, with
 * form and scroll state preserved) instead of being destroyed and rebuilt.
 *
 * Only routes flagged with `data: reusableRouteData` are cached; every other
 * route in the app falls through to the default behaviour, so this is safe to
 * provide globally.
 *
 * Cache entries are namespaced by `reportId` so one report can never show
 * another's cached tabs. Entries are destroyed when the report changes or when
 * navigation leaves the reporting area, so detached component trees (and their
 * form subscriptions) don't leak.
 */
@Injectable()
export class VesselReportRouteReuseStrategy
  implements RouteReuseStrategy, OnDestroy
{
  // Resolved lazily (not in the constructor) to avoid a Router <-> strategy DI
  // cycle: the Router injects the RouteReuseStrategy at construction.
  private readonly injector = inject(Injector);

  private readonly handlers = new Map<string, DetachedRouteHandle>();
  private currentReportId: string | null = null;
  private routerSub?: Subscription;

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.clearAll();
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    this.ensureRouterWatch();
    return this.isReusable(route);
  }

  store(
    route: ActivatedRouteSnapshot,
    handle: DetachedRouteHandle | null,
  ): void {
    if (!this.isReusable(route)) return;

    const key = this.key(route);
    if (handle) {
      // Defensive: the router's detach/store/retrieve/store(null) cycle means a
      // key should always be free before it is stored again. If that ever stops
      // holding, dropping the displaced handle silently would leak a whole tab
      // component tree, so destroy it on the way out.
      const displaced = this.handlers.get(key);
      if (displaced && displaced !== handle) this.destroyHandle(displaced);

      this.handlers.set(key, handle);
      return;
    }

    // Angular passes a null handle right after `retrieve()` reattaches a cached
    // component, to clear the slot (see the router's `activateRoutes`). The
    // component is now owned by the outlet again, so we must NOT destroy it —
    // just drop our reference. It is re-stored when the tab is next detached.
    this.handlers.delete(key);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    this.ensureRouterWatch();
    if (!this.isReusable(route)) return false;

    // Entering a tab of a different report: drop the previous report's cache.
    this.syncCurrentReport(route);

    return this.handlers.has(this.key(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.isReusable(route)) return null;
    return this.handlers.get(this.key(route)) ?? null;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    // Default Angular behaviour — anything broader breaks param updates.
    return future.routeConfig === curr.routeConfig;
  }

  private isReusable(route: ActivatedRouteSnapshot): boolean {
    return isReusableRoute(route.routeConfig);
  }

  private key(route: ActivatedRouteSnapshot): string {
    return `${this.reportId(route) ?? ''}::${route.routeConfig?.path ?? ''}`;
  }

  private reportId(route: ActivatedRouteSnapshot | null): string | null {
    let current = route;
    while (current) {
      const id = current.params?.['reportId'];
      if (id) return id;
      current = current.parent;
    }
    return null;
  }

  private syncCurrentReport(route: ActivatedRouteSnapshot): void {
    const reportId = this.reportId(route);
    if (!reportId || reportId === this.currentReportId) return;

    this.purgeReportsExcept(reportId);
    this.currentReportId = reportId;
  }

  private purgeReportsExcept(reportId: string): void {
    const prefix = `${reportId}::`;
    for (const [key, handle] of this.handlers) {
      if (key.startsWith(prefix)) continue;
      this.destroyHandle(handle);
      this.handlers.delete(key);
    }
  }

  private clearAll(): void {
    for (const handle of this.handlers.values()) {
      this.destroyHandle(handle);
    }
    this.handlers.clear();
    this.currentReportId = null;
  }

  /**
   * A DetachedRouteHandle wraps a live ComponentRef; destroy it to free the view.
   *
   * `DetachedRouteHandle` is opaque by contract, but the router stores
   * `{ componentRef, route, contexts }` (see `deactivateRouteAndItsChildren` in
   * `@angular/router`). Destroying the `ComponentRef` is the only way to free a
   * detached view, so we have to reach into that shape.
   *
   * Because it is not public API, an Angular upgrade that renames the field
   * would otherwise make this a silent no-op and leak every cached tab. Log
   * loudly if that ever happens — and `vessel-report-route-reuse.strategy.spec.ts`
   * drives a real Router through detach and purge, so a shape change fails in CI
   * rather than in production memory.
   */
  private destroyHandle(handle: DetachedRouteHandle): void {
    const componentRef = (handle as { componentRef?: { destroy: () => void } })
      ?.componentRef;

    if (!componentRef) {
      console.error(
        'VesselReportRouteReuseStrategy: a DetachedRouteHandle did not expose `componentRef`, ' +
          'so the cached vessel-report tab could not be destroyed and its view will leak. ' +
          'If this followed an Angular upgrade, update destroyHandle() to match the ' +
          "router's current handle shape.",
      );
      return;
    }

    componentRef.destroy();
  }

  /**
   * Watches navigation and drops the cache once it lands outside the report the
   * entries belong to, so cached tab trees don't linger after the user navigates
   * away. Set up lazily on first use, by which point the Router is safely
   * constructed.
   *
   * Scoped by the active `reportId` rather than by "is any reusable route
   * active", because the latter clears the cache on a non-reusable sibling tab.
   * Report History is such a tab — it is still the same report, so visiting it
   * must not force every other tab to be rebuilt on return. Comparing against
   * `currentReportId` also means another area that happens to use a `reportId`
   * param (offshore reports) still clears, rather than being mistaken for the
   * report we hold views for.
   */
  private ensureRouterWatch(): void {
    if (this.routerSub) return;

    const router = this.injector.get(Router);
    this.routerSub = router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe(() => {
        const activeReportId = this.activeReportId(
          router.routerState.snapshot.root,
        );

        if (!activeReportId || activeReportId !== this.currentReportId) {
          this.clearAll();
        }
      });
  }

  /** The `reportId` of the deepest active route that carries one, if any. */
  private activeReportId(route: ActivatedRouteSnapshot): string | null {
    for (const child of route.children) {
      const fromChild = this.activeReportId(child);
      if (fromChild) return fromChild;
    }

    return route.params?.['reportId'] ?? null;
  }
}
